import axios, { AxiosError } from 'axios'
import config from 'config'
import ApplicationProgress from 'state/enums/ApplicationProgress'
import ApplicationStatus from 'state/enums/ApplicationStatus'

import { OctaneLogger } from '../../integrations/datadog/OctaneLogger'
import { defaultLosJsonHeaders } from '../requestHeaders'

export type ApplicationSubmissionStatus = {
    id: number
    uuid: string
    status: `${ApplicationStatus}`
    progress: `${ApplicationProgress}`
    is_decision_pending: boolean
    is_submission_in_invalid_state: boolean
}

/**
 * Get an application submission status by its identifier.
 *
 * @param  {number | string} [uuid] Application UUID.
 * @param  {string} [pollUrl] Optional polling URL from submit response. Falls back to constructing URL from UUID.
 * @return {Object} Application data.
 */
export default async function getApplicationSubmissionStatus(
    uuid: string,
    pollUrl?: string,
): Promise<ApplicationSubmissionStatus> {
    try {
        if (!uuid && !pollUrl) {
            throw new Error('Application UUID or poll URL must be provided')
        }

        // Use poll URL if provided, otherwise construct from UUID (fallback to current behavior).
        // Strip origin from pollUrl if it is a full URL so we can always prepend API_BASE_URL.
        const maybeRelativePollUrl =
            pollUrl || `/api/v3/client-application/${uuid}/submission-status/`

        const pollUrlObject = new URL(maybeRelativePollUrl, config.API_BASE_URL)
        const relativePollUrl = `${pollUrlObject.pathname}${pollUrlObject.search}`
        const fullPollUrl = `${config.API_BASE_URL}${relativePollUrl}`

        const { data } = await axios.get(fullPollUrl, {
            withCredentials: true,
            headers: defaultLosJsonHeaders(),
        })

        return data as ApplicationSubmissionStatus
    } catch (error) {
        const axiosError = error as AxiosError

        OctaneLogger.axiosError(
            `Failed to get application submission status: ${axiosError.message}`,
            axiosError,
            { application: { uuid }, pollUrl },
        )

        throw axiosError
    }
}
